import {
  filterTasks,
  filterSubSuppliers,
  Filter,
  workflowTasks,
  DropDown,
} from 'mocks/data/dropdown';
import { HTTP_RESPONSE_CODES } from 'mocks/api';
import { taskUsers } from 'mocks/data/taskUser';
import { processTypes, suppliers } from 'mocks/data/suppliers';
import type { Server } from 'miragejs';
import { taskLinks } from 'mocks/data/task';
import { response } from 'mocks/helpers';
import { getFacilitiesData } from './facilities';
import { faker } from '@faker-js/faker/locale/en';
import {
  getCertificateStandardsForScope,
  type CertificateStandardScope,
} from 'mocks/data/certificateStandards';
import { getCertificateInstitutes } from 'mocks/data/certificateInstitutes';

const res = <T>(data: T) => {
  return {
    status: 'success',
    code: 'ok',
    error: null,
    data,
    statusCode: HTTP_RESPONSE_CODES.OK,
  };
};

/**
 * Supplier certificate modal — same shape as `SCMCertificateStandardsController` / `SCMCertificateInstitutesController`.
 * Registered always (see `mocks/api/index.ts`) so these paths work when the app is served without MVC.
 */
export const certificateSupplierMetaEndpoints = (server: Server) => {
  server.get('/api/scm-certificate-standards', (_schema, request) => {
    const certificateType = new URL(request.url).searchParams.get('certificateType');
    const scope: CertificateStandardScope | null =
      certificateType === 'facility' || certificateType === 'product' ? certificateType : null;
    const rows =
      scope === null
        ? []
        : getCertificateStandardsForScope(scope).map(s => ({
            id: s.apiId,
            name: s.label,
            certificateType: scope,
            category: null,
            description: null,
          }));
    return response(request.url, undefined, {
      body: res<
        {
          id: number;
          name: string;
          certificateType: string;
          category: null;
          description: null;
        }[]
      >(rows),
    });
  });
  server.get('/api/scm-certificate-institutes', (_schema, request) => {
    const rows = getCertificateInstitutes().map(row => ({
      id: row.apiId,
      name: row.label,
      instituteType: { id: 1, name: 'Certification body' },
      countryCode: null,
      uiCapabilities: ['single'] as const,
    }));
    return response(request.url, undefined, { body: res<typeof rows>(rows) });
  });
};

const rawBrandsData = Array.from({ length: faker.number.int({ min: 3, max: 8 }) }, (_, i) => ({
  id: faker.number.int({ min: 1, max: 200 }),
  name: faker.company.name(),
  state: faker.datatype.boolean(),
  address: faker.helpers.maybe(() => faker.location.streetAddress()) ?? null,
  zipCode: faker.helpers.maybe(() => faker.location.zipCode()) ?? null,
  city: faker.helpers.maybe(() => faker.location.city()) ?? null,
  stateOrRegion: faker.helpers.maybe(() => faker.location.state()) ?? null,
  countryCode: faker.helpers.maybe(() => faker.location.countryCode()) ?? null,
  deleted: false,
  userDefinedId: faker.helpers.maybe(() => String(i + 1)) ?? null,
}));

/**
 * `GET /api/brands` and every mock handler that assigns brands to rows (items) import
 * this single list, and the brand filter matches by `id`. Faker can emit the same `id`
 * for two brands, which would let the combobox show two distinct names that filter to
 * the same rows — picking the second name would return the first's rows. Guarantee unique
 * ids here, at the shared source, by bumping any duplicate to the next free value. This is
 * a pure post-process (no extra faker calls), so the global faker sequence — and therefore
 * every other seeded mock — is byte-for-byte unchanged.
 */
export const brandsData = (() => {
  const usedIds = new Set<number>();
  return rawBrandsData.map(brand => {
    let id = brand.id;
    while (usedIds.has(id)) id += 1;
    usedIds.add(id);
    return { ...brand, id };
  });
})();

export const genericEndpoints = (server: Server) => {
  server.get('/api/meta/filters/tasks', (schema, request) => {
    return response(request.url, undefined, { body: res<Filter[]>(filterTasks()) });
  });
  server.get('/api/meta/filters/workflow-tasks', (schema, request) => {
    return response(request.url, undefined, { body: res(workflowTasks(35)) });
  });
  server.get(`/api/suppliers`, (schema, request) => {
    const data = taskUsers.map(d => ({
      ...d,
      id: d.id,
      label: d.name,
    }));
    return response(request.url, undefined, { body: res(data) });
  });
  server.get(`/api/users`, (schema, request) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    if (!search) return response(request.url, undefined, { body: res(taskUsers) });
    const filtered = taskUsers.filter(u => u.name.toLowerCase().includes(search.toLowerCase()));
    return response(request.url, undefined, { body: res(filtered) });
  });
  server.get('/api/meta/filters/sub-suppliers', (schema, request) => {
    return response(request.url, undefined, { body: res<Filter[]>(filterSubSuppliers()) });
  });
  server.get('/api/meta/search/sub-suppliers', (schema, request) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const name = url.searchParams.get('name');
    const customId = url.searchParams.get('customId');
    const primaryContact = url.searchParams.get('primaryContact');

    let data = suppliers(2);

    // Filter by name parameter
    if (name) {
      data = data.filter(
        s => s.name.toLowerCase().includes(name.toLowerCase()) || s.id.toString().includes(name)
      );
    }

    // Filter by customId parameter
    if (customId) {
      data = data.filter(
        s =>
          s.customId?.toLowerCase().includes(customId.toLowerCase()) ||
          s.id.toString().includes(customId)
      );
    }

    // Filter by primaryContact parameter
    if (primaryContact) {
      data = data.filter(
        s =>
          s.contacts?.some(
            c =>
              `${c.firstName} ${c.lastName}`.toLowerCase().includes(primaryContact.toLowerCase()) ||
              c.email?.toLowerCase().includes(primaryContact.toLowerCase())
          )
      );
    }

    // Fallback to general search if no specific parameter
    if (search && !name && !customId && !primaryContact) {
      data = data.filter(
        u =>
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.id.toString().includes(search) ||
          u.customId?.toLowerCase().includes(search.toLowerCase())
      );
    }

    return response(request.url, undefined, { body: res(data) });
  });
  server.get('api/meta/filters/task-links/:search', (schema, request) => {
    return response(request.url, undefined, { body: res(taskLinks) });
  });

  // GET /api/meta/filters/{field} - Individual filter endpoints for taskboard
  server.get('/api/meta/filters/:field', (schema, request) => {
    const field = request.params.field as string;
    const filterData = filterTasks();
    const fieldFilter = filterData.find(
      f =>
        f.filterFieldName?.toLowerCase() === field.toLowerCase() ||
        f.filterFieldName?.toLowerCase() === `${field}category` ||
        f.filterFieldName?.toLowerCase() === `certificate${field}`
    );

    if (fieldFilter) {
      return response(request.url, undefined, { body: res(fieldFilter.options || []) });
    }

    // Return empty array if field not found
    return response(request.url, undefined, { body: res([]) });
  });

  // GET /api/meta/filters/brand - Brand filter
  server.get('/api/meta/filters/brand', (schema, request) => {
    const filterData = filterTasks();
    const brandFilter = filterData.find(f => f.filterFieldName === 'brand');
    return response(request.url, undefined, { body: res(brandFilter?.options || []) });
  });

  // GET /api/meta/filters/season - Season filter
  server.get('/api/meta/filters/season', (schema, request) => {
    const filterData = filterTasks();
    const seasonFilter = filterData.find(f => f.filterFieldName === 'season');
    return response(request.url, undefined, { body: res(seasonFilter?.options || []) });
  });

  // GET /api/meta/filters/styleCategory - Style category filter
  server.get('/api/meta/filters/styleCategory', (schema, request) => {
    const filterData = filterTasks();
    const styleCategoryFilter = filterData.find(f => f.filterFieldName === 'styleCategory');
    return response(request.url, undefined, { body: res(styleCategoryFilter?.options || []) });
  });

  // GET /api/meta/filters/itemCategory - Item category filter
  server.get('/api/meta/filters/itemCategory', (schema, request) => {
    const filterData = filterTasks();
    const itemCategoryFilter = filterData.find(f => f.filterFieldName === 'itemCategory');
    return response(request.url, undefined, { body: res(itemCategoryFilter?.options || []) });
  });

  // GET /api/meta/filters/certificateCategory - Certificate category filter
  server.get('/api/meta/filters/certificateCategory', (schema, request) => {
    const filterData = filterTasks();
    const certificateCategoryFilter = filterData.find(
      f =>
        f.filterFieldName === 'certificatecategory' || f.filterFieldName === 'certificateCategory'
    );
    return response(request.url, undefined, {
      body: res(certificateCategoryFilter?.options || []),
    });
  });

  // GET /api/meta/filters/styleGroup - Style group filter
  server.get('/api/meta/filters/styleGroup', (schema, request) => {
    const filterData = filterTasks();
    const styleGroupFilter = filterData.find(f => f.filterFieldName === 'styleGroup');
    return response(request.url, undefined, { body: res(styleGroupFilter?.options || []) });
  });

  // GET /api/postates - Purchase order states filter
  server.get('/api/postates', (schema, request) => {
    const states = [
      { id: 1, name: 'Draft', localeCode: 'postate_draft' },
      { id: 2, name: 'Pending', localeCode: 'postate_pending' },
      { id: 3, name: 'Approved', localeCode: 'postate_approved' },
      { id: 4, name: 'Rejected', localeCode: 'postate_rejected' },
      { id: 5, name: 'Cancelled', localeCode: 'postate_cancelled' },
      { id: 6, name: 'Completed', localeCode: 'postate_completed' },
    ];
    return response(request.url, undefined, { body: res(states) });
  });

  // GET /api/meta/filters/:orgIdOrSupplierId/facility - Facility filters endpoint
  server.get('/api/meta/filters/:orgIdOrSupplierId/facility', (schema, request) => {
    const orgIdOrSupplierId = request.params.orgIdOrSupplierId;
    const facilitiesData = getFacilitiesData(orgIdOrSupplierId);
    // Generate facility filter data structure
    const data: Filter[] = [
      {
        filterFieldId: 1,
        filterFieldName: 'name',
        options: facilitiesData.map(({ id, name, isArchived }) => ({
          id,
          displayName: name,
          value: name,
          isArchived,
        })),
      },
      {
        filterFieldId: 2,
        filterFieldName: 'processtype',
        options: processTypes.map(process => {
          return {
            ...process,
            displayName: process?.name,
            value: process?.name,
          };
        }) as unknown as DropDown[],
      },
      {
        filterFieldId: 3,
        filterFieldName: 'country',
        options: Array.from({ length: 5 }, (_, index) => ({
          id: String(index + 1),
          displayName: `Country ${index + 1}`,
          value: `country-${index + 1}`,
        })),
      },
      {
        filterFieldId: 4,
        filterFieldName: 'countryCode',
        options: [
          { id: 'US', displayName: 'US', value: 'US' },
          { id: 'CN', displayName: 'CN', value: 'CN' },
          { id: 'IN', displayName: 'IN', value: 'IN' },
          { id: 'BD', displayName: 'BD', value: 'BD' },
          { id: 'PK', displayName: 'PK', value: 'PK' },
        ],
      },
    ];

    return response(request.url, undefined, { body: res<Filter[]>(data) });
  });

  // GET /api/meta/search/facilities - Search facilities endpoint
  server.get('/api/meta/search/facilities', (schema, request) => {
    const search = request.queryParams.search as string;

    // Generate mock facility search results
    const facilities = Array.from({ length: 20 }, (_, index) => ({
      id: index + 1,
      name: `Facility ${index + 1}`,
      address: `Address ${index + 1}`,
      city: `City ${index + 1}`,
      countryCode: ['US', 'CN', 'IN', 'BD', 'PK'][index % 5],
      isArchived: index % 4 === 0,
    }));

    let data = facilities;
    if (search) {
      data = facilities.filter(
        f =>
          f.name.toLowerCase().includes(search.toLowerCase()) ||
          f.address?.toLowerCase().includes(search.toLowerCase()) ||
          f.city?.toLowerCase().includes(search.toLowerCase()) ||
          f.countryCode?.toLowerCase().includes(search.toLowerCase())
      );
    }

    return response(request.url, undefined, { body: res(data) });
  });

  server.get('/CurrencySeasonAssociation/:organizationId', (schema, request) => {
    const currencies = Array.from({ length: 5 }, () => {
      const code = faker.finance.currencyCode();
      return {
        Id: faker.number.int({ min: 1, max: 200 }),
        CountryCurrency: `${faker.location.country()} ${faker.finance.currencyName()}`,
        CurrencyCode: code,
        Value: parseFloat(faker.finance.amount({ min: 0.05, max: 2, dec: 5 })),
        IsActive: true,
        Name: null,
      };
    });
    return response(request.url, undefined, { body: currencies });
  });

  server.get('/TermsOfPayment', (schema, request) => {
    return response(request.url, undefined, {
      body: [
        {
          Id: 7,
          Name: 'CAD (Cash against documents)',
          Organization: null,
          IsDeleted: false,
          DeletedOn: null,
        },
        {
          Id: 8,
          Name: 'L/C (Letter of credit)',
          Organization: null,
          IsDeleted: false,
          DeletedOn: null,
        },
      ],
    });
  });

  server.get('/TermsOfDelivery', (schema, request) => {
    return response(request.url, undefined, {
      body: [
        {
          Id: 9,
          Name: 'DDP (Delivery duty paid)',
          Organization: null,
          IsDeleted: false,
          DeletedOn: null,
        },
        {
          Id: 10,
          Name: 'FOB (Free on board)',
          Organization: null,
          IsDeleted: false,
          DeletedOn: null,
        },
      ],
    });
  });

  server.get('/api/brands', (schema, request) => {
    // Honour the `search` param so the endpoint behaves like the real server-side
    // brand search (used by the settings user brand picker, dl-62402) rather than
    // returning the whole list for every query. No `search` → full list (the brand
    // filter comboboxes that don't search still get everything).
    const search = (request.queryParams.search as string) ?? '';
    const data = search
      ? brandsData.filter(brand => brand.name.toLowerCase().includes(search.toLowerCase()))
      : brandsData;
    return response(request.url, data);
  });
};
